import { FormEvent, useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { CircularProgress, Icon } from "@mui/material";

import "../../shared/css/ui.css";
import "../css/StripeCard.css";

type Props = {
  clientSecret: string;
  onCancel(): void;
  onSuccess(): void;
};

const cardStyle = {
  style: {
    base: {
      color: "#120d35",
      fontFamily: "Arial, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#8c86a8",
      },
    },
    invalid: {
      fontFamily: "Arial, sans-serif",
      color: "#e03131",
      iconColor: "#e03131",
    },
  },
};

export default function CheckoutForm(props: Props) {
  const { clientSecret, onCancel, onSuccess } = props;
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleChange = (event: StripeCardElementChangeEvent) => {
    // Display any errors as the customer types their card details
    setIsComplete(event.complete);
    setError(event.error ? event.error.message : null);
  };

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const card = elements?.getElement(CardElement);
    if (!stripe || !card) return;

    setProcessing(true);
    const payload = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card,
      },
    });
    setProcessing(false);

    if (payload.error) setError(`La mise à jour a échoué : ${payload.error.message}`);
    else onSuccess();
  };

  return (
    <form className="pf-card-form" onSubmit={handleSubmit} noValidate>
      <label className="pf-card-form__label" htmlFor="card-element">
        Nouvelle carte bancaire
      </label>
      <p className="pf-card-form__hint">Paiement sécurisé par Stripe. Votre carte n'est pas débitée maintenant.</p>
      <CardElement id="card-element" options={cardStyle} onChange={handleChange} />

      {error ? (
        <p className="nbk-feedback nbk-feedback--error" role="alert">
          <Icon aria-hidden="true">error_outline</Icon>
          <span>{error}</span>
        </p>
      ) : null}

      <div className="pf-card-form__actions">
        <button type="submit" className="nbk-btn nbk-btn--primary" disabled={processing || !isComplete || !stripe}>
          {processing ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : null}
          {processing ? "Enregistrement…" : "Enregistrer la carte"}
        </button>
        <button type="button" className="nbk-btn nbk-btn--ghost" onClick={onCancel} disabled={processing}>
          Annuler
        </button>
      </div>
    </form>
  );
}
